import Box from "@/common/components/Box";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { BriefcaseBusinessIcon, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { LoginValidationSchema, loginSchema } from "../../schemas/login.schema";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import MaskedTextField from "@/common/components/MaskedTextField";
import useLogin from "../../hooks/useLogin";
import { clearCompanyDomain, getCompanyDomain } from "@/common/services/companyDomainGetSet";
import ForgotPasswordModal from "../../components/ForgotPasswordModal";
import Button from "@/common/components/Button";
import usePublicDomain from "@/common/hooks/queries/usePublicDomain";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { twMerge } from "tailwind-merge";
import { PublicDomainData } from "@/common/types/publicDomain";
import { useQueryClient } from "@tanstack/react-query";

const Login = () => {
  const navigate = useNavigate();

  const [loginError, setLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [domainData, setDomainData] = useState<PublicDomainData>();

  const queryClient = useQueryClient();

  const location = useLocation();
  const from = location?.state?.from?.pathname || '/company';

  const { mutate: publicDomain } = usePublicDomain({
    onSuccess(response) {
      setDomainData(response.data.data);
    },
    onError() {
      clearCompanyDomain();
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<LoginValidationSchema>({
    resolver: zodResolver(loginSchema)
  });

  const { isPending, mutate } = useLogin({
    onSuccess: (data) => {
      setLoginError(false);

      queryClient.clear();

      if (data?.infos?.data?.temporary_password) {
        navigate('/reset-password', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    },
    onError: (error) => {
      setLoginError(true);

      const { errors: { base: errors } } = error?.response?.data as { errors: { base: string[]; }; };

      if (errors && errors.length) {
        setError('password', { message: errors[0] });
      }
    }
  });

  const onSubmit: SubmitHandler<LoginValidationSchema> = (data) => mutate(data);

  useEffect(() => {
    if (!getCompanyDomain()) {
      navigate('/', { replace: true });
    } else {
      publicDomain(getCompanyDomain());
    }
  }, []);

  return (
    <div className="flex flex-col items-center gap-8 w-screen">
      <img src="/Logo.svg" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className="flex flex-col gap-4 rounded-lg rounded-b-none backdrop-blur-lg w-[460px]">
          <div className="flex flex-col gap-2 text-center">
            <h3 className={twMerge('text-2xl font-bold', !domainData?.domainable?.general_information && 'hidden')}>
              Bem vindo a
            </h3>

            <div className={twMerge('flex justify-center items-center gap-2', !domainData?.domainable?.general_information && 'hidden')}>
              <ImageWithPlaceholder
                placeholderIcon={BriefcaseBusinessIcon}
                url={domainData?.domainable?.general_information?.company_logo?.url}
                iconClassName="size-4"
                className="size-6"
              />

              <h4 className="text-[18px]">
                {domainData?.domainable?.general_information?.fantasy_name}
              </h4>
            </div>

            <p className="font-light">
              Confirme seus dados para acessar a plataforma.
            </p>
          </div>

          <MaskedTextField
            mask="000.000.000-00"
            label="CPF"
            variant={errors.cpf?.message || loginError ? 'error' : 'default'}
            errorMessage={errors.cpf?.message || undefined}
            {...register('cpf')}
          />

          <TextField
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            RightIcon={showPassword ? <EyeOff /> : <Eye />}
            onIconClick={() => setShowPassword(!showPassword)}
            variant={errors.password?.message ? 'error' : 'default'}
            errorMessage={errors.password?.message || undefined}
            {...register('password')}
          />

          <div className="flex justify-end">
            <ForgotPasswordModal />
          </div>
        </Box>

        <Button
          data-testid="login_submit_button"
          type="submit"
          className="w-full bg-white-800 hover:bg-white-400 text-black rounded-b-lg rounded-t-md py-4"
          isLoading={isPending}
          spinnerClassName="fill-gray-800"
        >
          Entrar na conta
        </Button>
      </form>

      <div className="flex flex-col items-center">
        <span className="text-[10px] italic font-light">
          Desenvolvido por
        </span>

        <img src='/Avantsoft.svg' />
      </div>
    </div>
  );
};

export default Login;
